import React, { useEffect } from "react";
import FormLabel from "../../../shared/components/ui/FormLabel";
import Select from "../../../shared/components/ui/Select";
import Input from "../../../shared/components/ui/Input";
import Button from "../../../shared/components/ui/Button";
import { Box } from "../../../shared/components/ui/chakra";
import ArrowForwardIcon from "../../../shared/components/icons/ArrowForwardIcon";
import WandStarsIcon from "../../../shared/components/icons/WandStarsIcon";
import { Options } from "../../../../admin/shared/lib/types";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import MultiSelectForOptions from "../../owned_business_entities/components/MultiSelectForOptions";
import { newBusinessMatchingOwnedBusinessEntityPath } from "../../../../../routes";
import { objectToQuery } from "../../../../shared/lib/objectToQuery";

import { Text, ButtonGroup, Stack, HStack, Button as ChakraButton, Link, Grid } from "@chakra-ui/react";

type BusinessEntitySearchFormProps = {
  prefectures: Options;
  industries: Options;
  businessFields: Options;
  buildingTypes: Options;
  isDisabled?: boolean;
};

type UseCase = "contractor" | "client" | "";

const defaultValues = {
  use_case_eq: "" as UseCase,
  keyword_s_cont_all: "",
  prefecture_id_eq: "",
  industry_id_eq: "",
  contractor_info_supported_business_fields_id_in: [] as string[],
  contractor_info_supported_building_types_id_in: [] as string[],
  contractor_info_supported_prefectures_id_in: [] as string[],
  client_info_desired_industries_id_in: [] as string[],
  client_info_desired_business_fields_id_in: [] as string[],
};

export const BusinessEntitySearchForm = ({
  prefectures,
  industries,
  businessFields,
  buildingTypes,
  isDisabled = false,
}: BusinessEntitySearchFormProps) => {
  const methods = useForm({
    defaultValues,
  });
  const { control, watch, handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setValue("use_case_eq", (params.get("q[use_case_eq]") ?? "") as UseCase);
    setValue("keyword_s_cont_all", (params.get("q[keyword_s_cont_all]") ?? "") as UseCase);
    setValue("prefecture_id_eq", params.get("q[prefecture_id_eq]") ?? "");
    setValue("industry_id_eq", params.get("q[industry_id_eq]") ?? "");
    setValue(
      "contractor_info_supported_business_fields_id_in",
      params.getAll("q[contractor_info_supported_business_fields_id_in][]") ??
        [],
    );
    setValue(
      "contractor_info_supported_building_types_id_in",
      params.getAll("q[contractor_info_supported_building_types_id_in][]") ??
        [],
    );
    setValue(
      "contractor_info_supported_prefectures_id_in",
      params.getAll("q[contractor_info_supported_prefectures_id_in][]") ?? [],
    );
    setValue(
      "client_info_desired_industries_id_in",
      params.getAll("q[client_info_desired_industries_id_in][]") ?? [],
    );
    setValue(
      "client_info_desired_business_fields_id_in",
      params.getAll("q[client_info_desired_business_fields_id_in][]") ?? [],
    );
  }, [setValue]);

  const useCase = watch("use_case_eq");
  const onSubmit = (data: typeof defaultValues) => {
    if (data.use_case_eq === "contractor") {
      data.client_info_desired_industries_id_in = [];
      data.client_info_desired_business_fields_id_in = [];
    } else {
      data.contractor_info_supported_business_fields_id_in = [];
      data.contractor_info_supported_building_types_id_in = [];
      data.contractor_info_supported_prefectures_id_in = [];
    }
    const url = objectToQuery(data);
    url.searchParams.delete("page");
    location.href = url.toString();
  };

  return (
    <Stack
      bgColor={"white"}
      p={{ base: 4, md: 5 }}
      borderRadius="4px"
      gap={6}
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack gap={isDisabled ? 4 : 8}>
        <HStack gap={4} flexDir={{ base: "column", md: "row" }} w={"100%"}>
          <Stack gap={0.5} w={"100%"}>
            <FormLabel>事業者種別</FormLabel>
            <Controller
              name="use_case_eq"
              control={control}
              render={({ field }) => (
                <UseCaseSegmentedControl
                  selectedUseCase={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
          <Controller
            control={control}
            name="prefecture_id_eq"
            render={({ field }) => (
              <Select placeholder="選択してください" label="所在地" {...field}>
                {prefectures.map((prefecture) => (
                  <option key={prefecture.id} value={prefecture.id.toString()}>
                    {prefecture.name}
                  </option>
                ))}
              </Select>
            )}
          />
          <Controller
            control={control}
            name="industry_id_eq"
            render={({ field }) => (
              <Select placeholder="選択してください" label="主業種" {...field}>
                {industries.map((industry) => (
                  <option key={industry.id} value={industry.id.toString()}>
                    {industry.name}
                  </option>
                ))}
              </Select>
            )}
          />
        </HStack>
        {isDisabled ? (
          <Stack px={4} py={5} bgColor={"brand.50"} borderRadius="8px" gap={6}>
            <HStack gap={2} align={{ base: "start", md: "center" }}>
              <WandStarsIcon color={"brand.500"} boxSize={{ base: 8, md: 7 }} />
              <HStack
                gap={{ base: 1, md: 2 }}
                mt={"1px"}
                flexDir={{ base: "column", md: "row" }}
                align={{ base: "start", md: "center" }}
              >
                <Text fontWeight={"bold"} fontSize={{ base: "sm", md: "md" }}>
                  事業者申請を行うと、さまざまな条件で検索することができます。
                </Text>
                <HStack gap={0.5}>
                  <Link
                    fontSize={"sm"}
                    fontWeight={"bold"}
                    href={newBusinessMatchingOwnedBusinessEntityPath()}
                  >
                    事業者申請はこちら
                  </Link>
                  <ArrowForwardIcon color={"textLink"} boxSize={4} />
                </HStack>
              </HStack>
            </HStack>
            <AdditionalSearchForm
              useCase={useCase}
              isDisabled={isDisabled}
              methods={methods}
              businessFields={businessFields}
              industries={industries}
              buildingTypes={buildingTypes}
              prefectures={prefectures}
            />
          </Stack>
        ) : (
          <AdditionalSearchForm
            useCase={useCase}
            isDisabled={isDisabled}
            methods={methods}
            businessFields={businessFields}
            industries={industries}
            buildingTypes={buildingTypes}
            prefectures={prefectures}
          />
        )}
      </Stack>
      <HStack w={{ base: "full", md: "initial" }} ml={{ base: 0, md: "auto" }}>
        <Button
          variant={"outline"}
          minW={"fit-content"}
          w="full"
          px={4}
          type="button"
          onClick={() => reset()}
        >
          <Stack flexDir={{ base: "column", md: "row" }} gap={0}>
            <Text>入力内容を</Text>
            <Text>リセット</Text>
          </Stack>
        </Button>
        <Button minW={"fit-content"} w="full" px={4} type="submit">
          <Stack flexDir={{ base: "column", md: "row" }} gap={0}>
            <Text>入力内容で</Text>
            <Text>検索</Text>
          </Stack>
        </Button>
      </HStack>
    </Stack>
  );
};

const SegmentedControlItem = ({
  label,
  value,
  selectedUseCase,
  onSelect,
}: {
  label: string;
  value: UseCase;
  selectedUseCase: string;
  onSelect: (useCase: UseCase) => void;
}) => {
  const isActive = selectedUseCase === value;
  return (
    <ChakraButton
      variant="outline"
      borderColor={isActive ? "textLink" : "border.input"}
      borderRadius={"2px"}
      color={isActive ? "white" : "textBase"}
      fontWeight={isActive ? "bold" : "normal"}
      bg={isActive ? "textLink" : "white"}
      _hover={{ bg: isActive ? "textLink" : "brand.50" }}
      w={"50%"}
      py={2}
      onClick={() => onSelect(value)}
    >
      {label}
    </ChakraButton>
  );
};

const UseCaseSegmentedControl = ({
  selectedUseCase,
  onChange,
}: {
  selectedUseCase: string;
  onChange: (useCase: UseCase) => void;
}) => {
  const handleUseCaseChange = (useCase: UseCase) => {
    if (selectedUseCase !== useCase) {
      onChange(useCase);
    }
  };

  return (
    <ButtonGroup isAttached variant="outline" w={"100%"} minH={10}>
      <SegmentedControlItem
        label="受注側"
        value="contractor"
        selectedUseCase={selectedUseCase}
        onSelect={handleUseCaseChange}
      />
      <SegmentedControlItem
        label="発注側"
        value="client"
        selectedUseCase={selectedUseCase}
        onSelect={handleUseCaseChange}
      />
    </ButtonGroup>
  );
};

const AdditionalSearchForm = ({
  useCase,
  isDisabled,
  methods,
  businessFields,
  industries,
  buildingTypes,
  prefectures,
}: {
  useCase: string;
  isDisabled: boolean;
  methods: UseFormReturn<typeof defaultValues>;
  businessFields: Options;
  industries: Options;
  buildingTypes: Options;
  prefectures: Options;
}) => {
  const { control } = methods;

  return (
    <Stack gap={3}>
      {useCase === "contractor" && (
        <Stack gap={3}>
          <Stack gap={4}>
            <HStack gap={4}>
              <Text
                fontWeight={"bold"}
                color={"brand.500"}
                fontSize={"lg"}
                wordBreak={"keep-all"}
              >
                受注側の希望条件
              </Text>
              <Box
                w={"100%"}
                borderBottomColor={"brand.500"}
                borderBottomWidth={"1px"}
              />
            </HStack>
            <Grid
              gap={4}
              gridTemplateColumns={{
                base: "minmax(1fr, 100%)",
                md: "repeat(3, minmax(0, 1fr))",
              }}
            >
              <Stack gap={1}>
                <FormLabel color={isDisabled ? "textGray" : "textBase"}>
                  対応できる分野
                </FormLabel>
                <Controller
                  control={control}
                  name="contractor_info_supported_business_fields_id_in"
                  render={({ field: { ref, ...field } }) => (
                    <Box ref={ref} tabIndex={-1}>
                      <MultiSelectForOptions
                        placeholder={"選択してください"}
                        options={businessFields}
                        isDisabled={isDisabled}
                        {...field}
                      />
                    </Box>
                  )}
                />
              </Stack>
              <Stack gap={1}>
                <FormLabel color={isDisabled ? "textGray" : "textBase"}>
                  対応できる建物種別
                </FormLabel>
                <Controller
                  control={control}
                  name="contractor_info_supported_building_types_id_in"
                  render={({ field: { ref, ...field } }) => (
                    <Box ref={ref} tabIndex={-1}>
                      <MultiSelectForOptions
                        placeholder={"選択してください"}
                        options={buildingTypes}
                        isDisabled={isDisabled}
                        {...field}
                      />
                    </Box>
                  )}
                />
              </Stack>
              <Stack gap={1}>
                <FormLabel color={isDisabled ? "textGray" : "textBase"}>
                  対応可能エリア
                </FormLabel>
                <Controller
                  control={control}
                  name="contractor_info_supported_prefectures_id_in"
                  render={({ field: { ref, ...field } }) => (
                    <Box ref={ref} tabIndex={-1}>
                      <MultiSelectForOptions
                        placeholder={"選択してください"}
                        options={prefectures}
                        isDisabled={isDisabled}
                        {...field}
                      />
                    </Box>
                  )}
                />
              </Stack>
            </Grid>
          </Stack>
        </Stack>
      )}
      {useCase === "client" && (
        <Stack gap={3}>
          <Stack gap={4}>
            <HStack gap={4}>
              <Text
                fontWeight={"bold"}
                color={"brand.500"}
                fontSize={"lg"}
                wordBreak={"keep-all"}
              >
                発注側の希望条件
              </Text>
              <Box
                w={"100%"}
                borderBottomColor={"brand.500"}
                borderBottomWidth={"1px"}
              />
            </HStack>
            <Grid
              gap={4}
              gridTemplateColumns={{
                base: "100%",
                md: "repeat(2, minmax(0, 1fr))",
              }}
            >
              <Stack gap={1}>
                <FormLabel color={isDisabled ? "textGray" : "textBase"}>
                  発注したい業種
                </FormLabel>
                <Controller
                  control={control}
                  name="client_info_desired_industries_id_in"
                  render={({ field: { ref, ...field } }) => (
                    <Box ref={ref} tabIndex={-1}>
                      <MultiSelectForOptions
                        placeholder={"選択してください"}
                        options={industries}
                        isDisabled={isDisabled}
                        {...field}
                      />
                    </Box>
                  )}
                />
              </Stack>
              <Stack gap={1}>
                <FormLabel color={isDisabled ? "textGray" : "textBase"}>
                  発注したい分野
                </FormLabel>
                <Controller
                  control={control}
                  name="client_info_desired_business_fields_id_in"
                  render={({ field: { ref, ...field } }) => (
                    <Box ref={ref} tabIndex={-1}>
                      <MultiSelectForOptions
                        placeholder={"選択してください"}
                        options={businessFields}
                        isDisabled={isDisabled}
                        {...field}
                      />
                    </Box>
                  )}
                />
              </Stack>
            </Grid>
          </Stack>
        </Stack>
      )}
      <Stack gap={1}>
        <FormLabel color={isDisabled ? "textGray" : "textBase"}>
          フリーワード検索
        </FormLabel>
        <Controller
          control={control}
          name="keyword_s_cont_all"
          render={({ field }) => (
            <Input
              placeholder="気になるキーワードを入力"
              borderRadius={"2px"}
              isDisabled={isDisabled}
              h={10}
              {...field}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
